
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Component, forwardRef, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef, inject } from "@angular/core";
import { TaskOptionComponent } from "../task-option.component";
import { Project, Tag, ICompact } from "../../../../models";
import { Store } from "@ngrx/store";
import { TasksRootState, getTagList } from "../../../../store/index";
import { Subject } from "rxjs";
import { TaskRepository } from "../../../../repository/task.repository";
import { MessageTranslatorService } from "../../../../services";
import { TasksConstants } from "../../../../shared/task-constacts";
import { debounceTime, takeWhile } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { VncLibraryService, VNCChipsComponent, VNCInputComponent } from "@uxf/vnc-library";
import { FormControl } from "@angular/forms";
import { CommonUtil } from "src/app/common/utils/common.utils";
import { MatIcon } from "@angular/material/icon";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
    selector: "vp-vnctask-option-add-tags",
    providers: [{
            provide: TaskOptionComponent,
            // eslint-disable-next-line
            useExisting: forwardRef(() => TaskAddTagsOptionComponent)
        }],
    templateUrl: "option-add-tags.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [VNCChipsComponent, VNCInputComponent, MatIcon, TranslatePipe]
})
export class TaskAddTagsOptionComponent extends TaskOptionComponent implements OnInit, OnDestroy {
  private store = inject<Store<TasksRootState>>(Store);
  override changerDetectorRef: ChangeDetectorRef;
  private taskRepository = inject(TaskRepository);
  private vncLibaryService = inject(VncLibraryService);
  private messageTranslatorService = inject(MessageTranslatorService);
  override matDialog: MatDialog;

  override id: string = "add-tags";
  override triggerKey = "#";
  isAlive = true;
  searchTagsList: any[] = [];
  debouncer: Subject<any> = new Subject();
  tagText: string;
  tagsList: ICompact[] = [];
  selectedTab: number = 0;
  tagControl: FormControl = new FormControl("");
  tagSearchControl: FormControl = new FormControl("");
  searchTag: string = "";
  @ViewChild("tagInput", { static: false }) tagInput: ElementRef;

  constructor() {
    const changerDetectorRef = inject(ChangeDetectorRef);
    const matDialog = inject(MatDialog);

    super();

    this.changerDetectorRef = changerDetectorRef;
    this.matDialog = matDialog;
  }

  ngOnInit() {
    this.tagControl.valueChanges.pipe(debounceTime(300), takeWhile(() => this.isAlive)).subscribe(value => {
      this.tagText = value;
      this.changerDetectorRef.markForCheck();
    });
    this.tagSearchControl.valueChanges.pipe(debounceTime(300), takeWhile(() => this.isAlive)).subscribe(value => {
      this.searchTag = value;
      this.taskRepository.getSearchTags(this.searchTag).subscribe(tags => {
        this.searchTagsList = tags;
        if (this.searchTagsList?.length > 0) {
          this.searchTagsList.forEach(element => {
            if (!element.color_hex || element.color_hex === "") {
              element.color_hex = this.getRandomColor();
            }
          });
        }
        if (!tags && tags?.length === 0) {
          this.searchTagsList = [];
        }
        this.changerDetectorRef.markForCheck();
      });
    });
  }

  override getPayload() {
    let value = this.getTagValue();

    console.log("[TaskAddTagsOptionComponent][getPayload] value", value);
    console.log("[TaskAddTagsOptionComponent][getPayload] this.tagsList", this.tagsList);

    if (!value) {
      return { };
    }
    return { tags: value };
  }

  addTag(event) {
    this.searchTagsList = [];
    if (this.tagText.trim().length >= 2) {
      let separatedTagsValues = this.tagText.split(",");
      let isDuplicate = false;
      let failInlength = false;
      let failInSpace = false;
      let failedTags = [];

      let tagFromStore = [];
      this.store.select(getTagList)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(tags => {
        if (tags?.length > 0) {
          tagFromStore = tags;
        }
      });
      separatedTagsValues.forEach(tag => {
        if (tag.length < 2 || tag.length > 30) {
          failedTags.push(tag);
          failInlength = true;
        } else if (tag.trim().indexOf(" ") >= 0 ) {
          failedTags.push(tag);
          failInSpace = true;
        } else {
          let isTagAlreadyExist = false;
          this.tagsList.forEach(t => {
            if (t.name.toLowerCase() === tag.trim().toLowerCase()) {
              failedTags.push(tag.trim());
              isDuplicate = true;
              isTagAlreadyExist = true;
            }
          });
          if (tagFromStore.length > 0) {
            tagFromStore.forEach(t => {
              if (t.name === this.tagText.trim()) {
                failedTags.push(this.tagText.trim());
                isDuplicate = true;
                isTagAlreadyExist = true;
              }
            });
          }
          if (!isTagAlreadyExist) {
            this.tagsList.push({ "id": Tag.tempId(), "name": tag.trim(), color_hex: this.getRandomColor() });
          }
        }
      });

      // validate
      if (failInlength) {
        const message = this.messageTranslatorService.getMessage(TasksConstants.TAG_MIN_LENGTH);
        this.vncLibaryService.openSnackBar(message, "","", "", 3000, "bottom", "left").subscribe(res => {
        });
        this.tagInput.nativeElement.value = failedTags.toString();
        this.tagText = this.tagInput.nativeElement.value;
        this.changerDetectorRef.markForCheck();
      } else if (failInSpace) {
        const message = this.messageTranslatorService.getMessage(TasksConstants.TAG_SPACE_NOT_ALLOWED);
        this.vncLibaryService.openSnackBar(message, "","", "", 3000, "bottom", "left").subscribe(res => {
        });
        this.tagInput.nativeElement.value = failedTags.toString();
        this.changerDetectorRef.markForCheck();
      } else if (isDuplicate) {
        const message = this.messageTranslatorService.getMessage(TasksConstants.DUPLICATE_TAG_NAME);
        this.vncLibaryService.openSnackBar(message, "","", "", 3000, "bottom", "left").subscribe(res => {
        });
        this.tagInput.nativeElement.value = failedTags.toString();
        this.changerDetectorRef.markForCheck();
      } else {
        console.log("[TaskAddTagsOptionComponent][addTag] success");

        this.tagText = "";
        this.tagControl.setValue("");
        this.changerDetectorRef.markForCheck();
      }
    }
  }

  removeTag(tagNameOrObj) {
    console.log("[TaskAddTagsOptionComponent][removeTag] tagNameOrObj", tagNameOrObj);
    console.log("[TaskAddTagsOptionComponent][removeTag] tagsList before", this.tagsList);

    let tagName;
    if (typeof tagNameOrObj === "string") {
      tagName = tagNameOrObj;
    } else {
      tagName = tagNameOrObj.name;
    }

    this.tagsList = this.tagsList.filter(item => item.name !== tagName);

    console.log("[TaskAddTagsOptionComponent][removeTag] tagsList after", this.tagsList);

    this.onRemoveTag(tagName);
  }

  setTags() {
    console.log("[TaskAddTagsOptionComponent][setTags] setTags", this.tagsList);

    this.onTagClick(this.tagsList);
    this.resetTagList();
  }

  resetTagList() {
    console.log("[TaskAddTagsOptionComponent][resetTagList]");

    this.tagsList = [];
    this.tagText = "";
    this.searchTag = "";
    this.searchTagsList = [];
    this.tagControl.setValue("");
    this.tagSearchControl.setValue("");
    this.tagInput.nativeElement.value = "";
    this.changerDetectorRef.markForCheck();
  }

  override ngOnDestroy() {
    this.isAlive = false;
    this.tagText = "";
    this.searchTag = "";
    this.tagsList = [];
    this.searchTagsList = [];
    this.tagControl.setValue("");
    this.tagSearchControl.setValue("");
  }

  backToMain() {
    this.resetTagList();
    this.goBack();
  }

  isValidTagName(): boolean {
    if ((this.tagControl.value?.length > 0) && (this.tagControl?.value.indexOf("\"") === -1)) {
      return true;
    } else {
      return false;
    }
  }

  addTagToAssign(tag) {
    let isDuplicate = false;
    let selectedTagName = tag?.name;
    this.tagsList.forEach(t => {
      if (t.name.toLowerCase() === selectedTagName.toLowerCase()) {
        const message = this.messageTranslatorService.getMessage(TasksConstants.DUPLICATE_TAG_NAME);
        this.vncLibaryService.openSnackBar(message, "","", "", 3000, "bottom", "left").subscribe(res => {
        });
        isDuplicate = true;
        return false;
      }
    });

    if (!isDuplicate) {
      for (let sTag of this.searchTagsList) {
        if (sTag.name.toLowerCase()  === selectedTagName.trim().toLowerCase()) {
          // reuse existing tag id
          this.tagsList.push(tag);
          break;
        }
      }
    }
  }


  onMouseOver(tag): void {
    let tagEl = document.getElementById("tag-selected-" + tag.id);
    const backgroundColor = window.getComputedStyle(tagEl, null).getPropertyValue("background-color");
    const textColor = window.getComputedStyle(tagEl, null).getPropertyValue("color");
    tagEl.style.backgroundColor = textColor;
    tagEl.style.color = backgroundColor;
    tagEl.style.outline = "solid 1px " + backgroundColor;
  }

  onMouseOut(tag): void {
    let tagEl = document.getElementById("tag-selected-" + tag.id);
    const backgroundColor = window.getComputedStyle(tagEl, null).getPropertyValue("background-color");
    const textColor = window.getComputedStyle(tagEl, null).getPropertyValue("color");
    tagEl.style.backgroundColor = textColor;
    tagEl.style.color = backgroundColor;
  }

  getTagColor(color) {
    const isDark = CommonUtil.lightOrDark(color) === "dark";
    if (isDark) {
        return "#ffffff";
    } else {
        return "#000000";
    }
  }

  getRandomColor(): string {
    const palette = [
      "#A8B0B9",
      "#00B8D4",
      "#388EDA",
      "#6358C7",
      "#F24336",
      "#FFAD16",
      "#27AB7F"
    ];

    const randomIndex = Math.floor(Math.random() * palette.length);
    return palette[randomIndex];
  }


}
